export const REENTRY_PLANS_LIST_PATH = '/reentry-plans';

export const CASE_PLAN_CREATE_PATH = '/case-plans/create';

export const CASE_PLAN_SECTION_SKELETON_COUNT = 3;

export const buildReentryPlanDetailsPath = (casePlanId) =>
  `${REENTRY_PLANS_LIST_PATH}/details/${casePlanId}`;

export const REENTRY_PLANNING_TABS = {
  MY_PLANS: 'My Plans',
  ALL_PLANS: 'All Plans',
};

/** `scope` values accepted by GET /api/admin/case-plans. */
export const REENTRY_PLANS_LIST_SCOPES = {
  MY: 'my',
  ALL: 'all',
};

/**
 * Tab metadata for the Re-Entry Planning shell. The label doubles as the table
 * heading, `listScope` drives the case-plans list query, and the ids keep the
 * tabpanel wired to its tab for screen readers.
 * Order matters: the first entry is the default tab.
 */
export const REENTRY_PLANNING_TAB_CONFIG = [
  {
    label: REENTRY_PLANNING_TABS.MY_PLANS,
    listScope: REENTRY_PLANS_LIST_SCOPES.MY,
    panelId: 'my-plans-panel',
    tabId: 'my-plans-tab',
  },
  {
    label: REENTRY_PLANNING_TABS.ALL_PLANS,
    listScope: REENTRY_PLANS_LIST_SCOPES.ALL,
    panelId: 'all-plans-panel',
    tabId: 'all-plans-tab',
  },
];

export const CASE_PLAN_PREVIEW_RELEASE_PERMISSION = 'case_plan_preview_release_access';
/** Prefix matching every `case_plan_reentry*` product slug. */
export const CASE_PLAN_REENTRY_PERMISSION_PREFIX = 'case_plan_reentry';
/** Any-of preview-release slugs used with CBAC `isAnyPermitted`. */
export const CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS = [CASE_PLAN_PREVIEW_RELEASE_PERMISSION];
/** Create / provision a reentry plan (Create New Plan button + /case-plans/create); the Create Plans product. */
export const CASE_PLAN_REENTRY_CREATE_PERMISSION = 'case_plan_reentry_create';
/** Create a reentry plan template; the Create Template product. */
export const CASE_PLAN_REENTRY_CREATE_TEMPLATE_PERMISSION = 'case_plan_reentry_create_template';

/** Open plan detail for plans the staff member is assigned to as an editor; View and Edit Plans product. */
export const CASE_PLAN_REENTRY_VIEW_ASSIGNED_DETAIL_PERMISSION =
  'case_plan_reentry_view_assigned_detail';
/**
 * Edit plan detail; a write slug of the View and Edit Plans product, never a read key.
 * Also identifies staff eligible to be plan editors (search-staff permissions filter).
 */
export const CASE_PLAN_REENTRY_EDIT_DETAIL_PERMISSION = 'case_plan_reentry_edit_detail';
/** Open any plan from All Plans, including plans the staff member does not edit; View All Plans product. */
export const CASE_PLAN_REENTRY_VIEW_ANY_DETAIL_PERMISSION = 'case_plan_reentry_view_any_detail';
/** Add oneself as a plan editor; a self-scoped membership write slug, never a read key. */
export const CASE_PLAN_REENTRY_SELF_ADD_EDITOR_PERMISSION = 'case_plan_reentry_self_add_editor';

/**
 * Permission that shows the My Plans tab and My Plans View Plan.
 * Canonical only: `case_plan_reentry_view_assigned_detail`.
 * Preview release stays on All Plans and must not unlock My Plans.
 */
export const CASE_PLAN_MY_PLANS_PERMISSIONS = [CASE_PLAN_REENTRY_VIEW_ASSIGNED_DETAIL_PERMISSION];

/**
 * Reentry LFA + this permission unlocks View Plan on every All Plans row.
 * Assigned-detail unlocks View Plan on editor rows via `isEditor`, not this list.
 */
export const CASE_PLAN_REENTRY_ALL_PLANS_VIEW_PERMISSIONS = [
  CASE_PLAN_REENTRY_VIEW_ANY_DETAIL_PERMISSION,
];

/**
 * Any-of permissions that unlock the Re-Entry Planning module, All Plans tab,
 * list API, and plan detail routes.
 * Canonical: `case_plan_reentry_view_assigned_detail`.
 * Transition fallback: `case_plan_preview_release_access` (Preview Release All Plans).
 * Write slugs and `view_any_detail` must not open the module.
 */
export const CASE_PLAN_ACCESS_PERMISSIONS = [
  CASE_PLAN_REENTRY_VIEW_ASSIGNED_DETAIL_PERMISSION,
  CASE_PLAN_PREVIEW_RELEASE_PERMISSION,
];

/**
 * Any-of permissions that let a staff member open ANY plan from All Plans, including plans they
 * do not edit. Deliberately narrower than CASE_PLAN_ACCESS_PERMISSIONS: the view-assigned
 * slug is excluded, because holding it alone only grants access to the staff member's own
 * plans (opened from My Plans). Write slugs are not read keys.
 * Transition fallback: `case_plan_preview_release_access`.
 */
export const CASE_PLAN_DETAIL_ALL_PLANS_PERMISSIONS = [
  CASE_PLAN_REENTRY_VIEW_ANY_DETAIL_PERMISSION,
  CASE_PLAN_PREVIEW_RELEASE_PERMISSION,
];

/** Any-of create permissions for the Create New Plan action and the /case-plans/create route. */
export const CASE_PLAN_CREATE_PERMISSIONS = [CASE_PLAN_REENTRY_CREATE_PERMISSION];

export const CASE_PLAN_EXPORT_SUCCESS_MESSAGE =
  'Your export has been queued. You will receive the export via email.';

export const PLAN_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  RELEASED: 'released',
  ARCHIVED: 'archived',
};

export const PLAN_STATUSES = [
  PLAN_STATUS.DRAFT,
  PLAN_STATUS.ACTIVE,
  PLAN_STATUS.RELEASED,
  PLAN_STATUS.ARCHIVED,
];

export const PLAN_STATUS_LABELS = {
  [PLAN_STATUS.DRAFT]: 'Draft',
  [PLAN_STATUS.ACTIVE]: 'Active',
  [PLAN_STATUS.RELEASED]: 'Released',
  [PLAN_STATUS.ARCHIVED]: 'Archived',
};

export const PLAN_STATUS_CHIP_LABEL = 'PLAN STATUS';

/** Dump tones for PlanStatusChip. Commons does not know plan statuses. */
export const PLAN_STATUS_CHIP_TONES = {
  [PLAN_STATUS.DRAFT]: 'neutral',
  [PLAN_STATUS.ACTIVE]: 'info',
  [PLAN_STATUS.RELEASED]: 'success',
  [PLAN_STATUS.ARCHIVED]: 'warning',
};

/**
 * Mirrors the backend transition rule: any status is allowed except
 * moving back to draft once the plan has left it. Same-status is a no-op.
 */
export const canTransitionStatus = (current, target) => {
  if (!PLAN_STATUSES.includes(target)) {
    return false;
  }

  if (target === current) {
    return true;
  }

  if (target === PLAN_STATUS.DRAFT && current !== PLAN_STATUS.DRAFT) {
    return false;
  }

  return true;
};

export const PLAN_STATUS_LEARNER_NAME_PLACEHOLDER = '{learnerName}';

export const PLAN_STATUS_CONFIRM_COPY = {
  [PLAN_STATUS.ACTIVE]: {
    heading: 'Ready to activate this plan?',
    body: `Once active, any section marked visible to learner will be shared with ${PLAN_STATUS_LEARNER_NAME_PLACEHOLDER} immediately.\n\nSections can no longer be removed and will be archived instead. Archived sections stay hidden from the plan but can be restored at any time.`,
    confirm: 'Activate Plan',
  },
  [PLAN_STATUS.ARCHIVED]: {
    heading: 'Archive this plan?',
    body: 'The learner will no longer see the plan, and editing closes for everyone. Nothing is deleted.',
    confirm: 'Archive Plan',
  },
  [PLAN_STATUS.RELEASED]: {
    heading: 'Mark as Released?',
    body: `You're marking ${PLAN_STATUS_LEARNER_NAME_PLACEHOLDER} as released. Congratulations to them on this milestone. The plan will be closed out and marked as complete.`,
    confirm: 'Mark as Released',
  },
};

export const PLAN_STATUS_UPDATE_SUCCESS_MESSAGE = 'Plan status updated.';
export const PLAN_STATUS_UPDATE_FORBIDDEN_MESSAGE =
  "You do not have permission to change this plan's status.";
export const PLAN_STATUS_UPDATE_INVALID_MESSAGE =
  'This status change is not allowed. The plan status has been refreshed.';
export const PLAN_STATUS_UPDATE_FAILED_MESSAGE =
  'Failed to update plan status. Please try again.';

export const WIDGET_DESCRIPTION_UPDATE_FAILED_MESSAGE =
  'Failed to update widget description. Please try again.';

/**
 * `case_plan.status` values. Section writes are allowed on Draft, Active, and Released only —
 * an Archived plan (and a template, which carries no status) allows none.
 */
export const CASE_PLAN_STATUSES = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  RELEASED: 'released',
  ARCHIVED: 'archived',
};

/**
 * `case_plan_section.status`. The detail response carries active and archived only, and those
 * two are what the tabs split. `DELETED` is terminal — the API stops listing such a section
 * altogether — so it exists here to recognize the Delete response and keep that section off
 * both tabs; there is no Deleted tab and no recovery.
 */
export const CASE_PLAN_SECTION_STATUSES = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DELETED: 'deleted',
};

/**
 * Destructive footer action a plan status permits. Draft soft-deletes, Active and Released
 * archive, and the two are mutually exclusive — a section never offers both.
 */
export const CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS = {
  DELETE: 'delete',
  ARCHIVE: 'archive',
};

/** Any-of permissions that unlock section management writes. Read slugs must not appear here. */
export const CASE_PLAN_SECTION_WRITE_PERMISSIONS = [CASE_PLAN_REENTRY_EDIT_DETAIL_PERMISSION];

/** UI move-arrow directions; translated to a target `sortOrder` before POST /sections/:sectionId/move. */
export const CASE_PLAN_SECTION_MOVE_DIRECTIONS = {
  UP: 'up',
  DOWN: 'down',
};

/**
 * Section list tabs on plan detail; the first entry is the default tab. Deleted sections get
 * no tab of their own — Delete is terminal, so they are simply gone.
 */
export const CASE_PLAN_SECTION_TABS = [
  {
    label: 'Active',
    value: CASE_PLAN_SECTION_STATUSES.ACTIVE,
    panelId: 'active-sections-panel',
    tabId: 'active-sections-tab',
  },
  {
    label: 'Archived',
    value: CASE_PLAN_SECTION_STATUSES.ARCHIVED,
    panelId: 'archived-sections-panel',
    tabId: 'archived-sections-tab',
  },
];

export const [DEFAULT_CASE_PLAN_SECTION_TAB] = CASE_PLAN_SECTION_TABS;

/**
 * Confirmation copy for the destructive section actions, adapted from the shared widget
 * confirmation design. `[section title]` is substituted at render time.
 *
 * The two outcomes differ and the copy has to say so: Archive keeps the section on the plan
 * under Archived sections, while Delete is terminal and offers no way back. `modalStatus` is
 * the shared confirmation modal treatment — Delete is the destructive (danger) one, Archive
 * is not.
 */
export const CASE_PLAN_SECTION_CONFIRMATION_COPY = {
  [CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.DELETE]: {
    modalStatus: 'danger',
    buildTitle: (sectionTitle) => `Delete ${sectionTitle}`,
    description:
      'This will remove the section and everything inside it from the active plan, and the learner will no longer see it. This cannot be undone.',
  },
  [CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.ARCHIVE]: {
    modalStatus: 'info',
    buildTitle: (sectionTitle) => `Archive ${sectionTitle}`,
    description:
      'This will remove the section and everything inside it from the active plan, and the learner will no longer see it. It will be saved in Archived sections.',
  },
};

/** Fallback used in confirmation copy while a freshly created section is still untitled. */
export const CASE_PLAN_SECTION_FALLBACK_TITLE = 'this section';

/**
 * Title `POST /sections` persists for a new section. The placeholder row rendered before the
 * response arrives must show the same text, so settling the create never changes the title
 * under the admin's cursor.
 */
export const CASE_PLAN_SECTION_PLACEHOLDER_TITLE = 'Section Title';

/**
 * Confirmation toasts for the destructive section actions. Both leave the admin on the
 * Active tab, where the section has just disappeared, so the toast is the only feedback
 * that the action succeeded.
 */
export const CASE_PLAN_SECTION_SUCCESS_MESSAGES = {
  DELETE: 'Section deleted.',
  ARCHIVE: 'Section archived. You can view it on the Archived tab.',
};

export const CASE_PLAN_SECTION_ERROR_MESSAGES = {
  CREATE: 'Failed to add the section. Please try again.',
  UPDATE: 'Failed to save the section. Please try again.',
  MOVE: 'Failed to reorder the section. Please try again.',
  DELETE: 'Failed to delete the section. Please try again.',
  ARCHIVE: 'Failed to archive the section. Please try again.',
  DEPENDENT: 'This section cannot be deleted while another active section depends on it.',
  BLANK_TITLE: 'Section title cannot be empty.',
};

/**
 * Single source of truth for reentry plan type keys.
 * - feature: LOCATIONS.activeFeatures / side-nav
 * - planType: case-plans API planTypes query + plan.planType
 */
export const REENTRY_PLAN_TYPES = {
  PREVIEW: { feature: 'reentry_plan_preview', planType: 'reentry_preview' },
  MVP: { feature: 'reentry_plan', planType: 'reentry' },
};

const REENTRY_PLAN_TYPE_ENTRIES = Object.values(REENTRY_PLAN_TYPES);

/** Ordered feature keys used for module access / entitlement checks */
export const REENTRY_VISIBILITY_PLAN_TYPES = REENTRY_PLAN_TYPE_ENTRIES.map(
  ({ feature }) => feature,
);

/** Normalizes a feature or planType key to the visibility (feature) key */
export const toVisibilityPlanType = (planTypeKey) => {
  if (planTypeKey == null || planTypeKey === '') {
    return undefined;
  }

  const normalized = String(planTypeKey).trim().toLowerCase();
  return REENTRY_PLAN_TYPE_ENTRIES.find(
    ({ feature, planType }) =>
      feature.toLowerCase() === normalized ||
      planType.toLowerCase() === normalized,
  )?.feature;
};

/** Maps entitled feature keys (or planType aliases) to case-plan API planType values */
export const mapEntitledPlanTypesToApi = (featureKeys = []) =>
  featureKeys
    .map((featureKey) => {
      const visibilityKey = toVisibilityPlanType(featureKey);
      return REENTRY_PLAN_TYPE_ENTRIES.find(
        ({ feature }) => feature === visibilityKey,
      )?.planType;
    })
    .filter(Boolean);

/**
 * Preview Empty State copy (CC-15220). Shown on All Plans when a facility has the
 * Preview entitlement (MVP off) and no learner has saved a job or resource yet.
 * Text must match the Figma design exactly.
 */
export const REENTRY_PREVIEW_EMPTY_STATE_HEADER =
  'See what learners are saving in the Honest Jobs Reentry Tool';
export const REENTRY_PREVIEW_EMPTY_STATE_BODY =
  'Use this page to view the jobs and resources learners have saved. No one at your facilities has saved anything yet. Once they do, their plans will appear here.';

export const REENTRY_PLANS_DEFAULT_PAGE_SIZE = 25;

export const REENTRY_PLANS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const VIEW_PLAN_LINK_COLOR = 'var(--tonal-palette-cerulean-main-20, #0078A2)';

/** Pendo tracking id for the Create New Plan action on the All Plans table */
export const REENTRY_CREATE_NEW_PLAN_BUTTON_ID = 'create-new-plan-button';

/** Modal detail keys for Honest Jobs community resources from reentry plan `resources` groups. */
export const HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS = [
  'Eligibility',
  'Availability',
  'Description',
  'We offer',
  'Languages',
  'Cost',
  'Next Steps',
  'Website',
  'Facebook',
  'Coverage Area',
  'Required',
  'Last Reviewed',
];

/** Modal detail keys for Honest Jobs job listings from reentry plan `jobs` groups. */
export const HONEST_JOBS_JOB_DETAIL_FIELD_KEYS = [
  'Pay Rate',
  'Frequency',
  'Education Level',
  'Description',
];
